import '@/styles/globals.css';
import type { AppProps } from 'next/app';
import NextApp from 'next/app';
import { AppContext } from 'next/dist/pages/_app';
import { getLocaleCode, getLocaleMessages } from '../../i18n/locale';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { Wrapper } from '@/components/_shared/Wrapper';
import { IncomingMessage } from 'http';
import { useAWSRum } from '../../common/monitoring/awsrum';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { BaseProps } from '@/types';
import { DatadogInit } from '../../common/monitoring/datadogrum';

type DeploymentProps = {
  locale: string;
  messages: Record<string, string>;
  serverUrl: string;
  rumGuestRoleArn: string;
  identityPoolId: string;
  applicationIds: Record<string, string>;
} & BaseProps;

export default function App({
  Component,
  pageProps,
  locale,
  messages,
  serverUrl,
  stage,
  rumGuestRoleArn,
  identityPoolId,
  applicationIds,
}: AppProps & DeploymentProps) {
  useAWSRum({
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  });
  return (
    <QueryClientProvider>
      <IntlProviderWrapper messages={messages} locale={locale}>
        <Wrapper serverUrl={serverUrl} stage={stage} locale={locale}>
          <DatadogInit stage={stage} />
          <ConsentManagerScripts />
          <Component {...pageProps} stage={stage} />
        </Wrapper>
      </IntlProviderWrapper>
    </QueryClientProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage;
  const locale = await getLocaleCode(req);
  const messages = await getLocaleMessages(req);
  const appProps = await NextApp.getInitialProps(context);

  // Extract serverUrl using robust getDomainUrl function
  const serverUrl = getDomainUrl(req) || getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  // Add RUM configuration - these should come from environment variables or CDK outputs
  const rumGuestRoleArn = process.env.RUM_GUEST_ROLE_ARN || '';
  const identityPoolId = process.env.IDENTITY_POOL_ID || '';
  const applicationIds = process.env.APPLICATION_IDS ? JSON.parse(process.env.APPLICATION_IDS) : {};

  return {
    ...appProps,
    locale,
    messages,
    serverUrl,
    stage,
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  };
};
